import MenuItem from '@material-ui/core/MenuItem';
import { getProjectConfig } from '../../getProjectConfig';

type QuantityOptionsConfig = {
  cap?: number;            // Extra per-item cap (maxSellItems).
  floor?: number;          // Lowest ceiling, so a selected quantity keeps its option.
  fallbackToOne?: boolean; // Emit a "1" when there is no stock (catalog behavior).
};

/**
 * Build the list of <MenuItem> for a quantity <Select>.
 *
 * `available` is only an upper bound and is never shown, so the real stock cannot be
 * deduced from the option count. Availability calculations must keep returning the
 * real value: row visibility and the disabled state depend on them.
 */
export const renderQuantityOptions = (available: number, config: QuantityOptionsConfig = {}) => {
  const { cap, floor = 0, fallbackToOne = false } = config;

  let limit = Number(getProjectConfig().MAX_QUANTITY);
  if (cap !== undefined && cap > 0) limit = Math.min(limit, cap);
  limit = Math.min(available, limit);
  limit = Math.max(limit, floor);

  const items = [];
  for (var i = 1; i <= limit; i++) {
    items.push(<MenuItem key={i} value={i}>{i}</MenuItem>)
  }
  if (items.length === 0 && fallbackToOne) {
    items.push(<MenuItem key={1} value={1}>{1}</MenuItem>)
  }
  return items;
};

/**
 * Apparent quantity of a catalog row: the catalog behaves as if total stock were
 * MAX_QUANTITY, counting down from the cap as the customer adds to the cart.
 *
 * `baselines` snapshots real availability the first time a row is seen. It lives in a
 * component ref keyed by itemId, so a reload resets it and a reused instance does not
 * inherit another item's snapshot.
 */
export const getApparentQuantity = (
  available: number,
  baselines: Record<string, number>,
  key: string,
) => {
  if (baselines[key] === undefined) baselines[key] = available;
  const baseline = baselines[key];

  const cap = Math.min(Number(getProjectConfig().MAX_QUANTITY), baseline);
  const added = Math.max(0, baseline - available);

  return Math.max(0, Math.min(cap - added, available));
};

export default renderQuantityOptions;
